import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Search,
  Banknote,
  PlusCircle,
  ArrowRight,
  ShieldAlert,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageGuard } from '@components/PageGuard';
import { TreasuryOperationModal } from '@components/finance/TreasuryOperationModal';
import { OwnerPicker, OwnerTypeToggle } from '@components/finance/OwnerPicker';
import type { OwnerSearchResult } from '@services/search.service';
import {
  ledgerService,
  WALLET_TYPE_CODES,
  type LedgerWallet,
  type WalletOwnerType,
} from '@services/ledger.service';
import {
  treasuryService,
  type ProvisionableOwnerType,
} from '@services/treasury.service';
import { cn, formatCurrency } from '@lib/utils';
import { humanizeCode, ownerTypeLabel } from './ledgerFormat';

const PAGE_SIZE = 25;

const inputClass =
  'rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors';

const labelClass =
  'block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5';

const thClass =
  'text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] whitespace-nowrap';

function errMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const m = (err as { message?: unknown }).message;
    if (typeof m === 'string' && m.trim()) return m;
  }
  return fallback;
}

export const TreasuryPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Wallet finder
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [ownerType, setOwnerType] = useState<'' | WalletOwnerType>('');
  const [walletTypeCode, setWalletTypeCode] = useState('');
  const [page, setPage] = useState(1);
  const [target, setTarget] = useState<LedgerWallet | null>(null);

  // Provisioning — owner is searched by name, never pasted as a UUID.
  const [provisionOwnerType, setProvisionOwnerType] =
    useState<ProvisionableOwnerType>('business');
  const [provisionOwner, setProvisionOwner] = useState<OwnerSearchResult | null>(null);
  const [provisionTypeCode, setProvisionTypeCode] = useState('');

  // Owner ledger jump
  const [jumpOwnerType, setJumpOwnerType] = useState<ProvisionableOwnerType>('business');
  const [jumpOwner, setJumpOwner] = useState<OwnerSearchResult | null>(null);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['ledger', 'wallets', 'treasury-finder', ownerType, walletTypeCode, q, page],
    queryFn: () =>
      ledgerService.listWallets({
        ownerType: ownerType || undefined,
        walletTypeCode: walletTypeCode || undefined,
        q: q || undefined,
        page,
        size: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const { data: walletTypes } = useQuery({
    queryKey: ['treasury', 'wallet-types'],
    queryFn: () => treasuryService.listWalletTypes(),
  });

  const provision = useMutation({
    mutationFn: () =>
      treasuryService.provisionWallet({
        ownerType: provisionOwnerType,
        ownerId: provisionOwner!.ownerId,
        walletTypeCode: provisionTypeCode,
      }),
    onSuccess: (result) => {
      toast.success(
        result.created
          ? `${humanizeCode(provisionTypeCode)} wallet created for ${provisionOwner?.label ?? 'this owner'}.`
          : `${humanizeCode(provisionTypeCode)} wallet already existed — nothing changed.`,
      );
      queryClient.invalidateQueries({ queryKey: ['ledger'] });
      queryClient.invalidateQueries({ queryKey: ['treasury'] });
    },
    onError: (err) => toast.error(errMessage(err, 'Could not provision that wallet.')),
  });

  const wallets = data?.data ?? [];
  const totalCount = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const canProvision = !!provisionOwner && !!provisionTypeCode;

  // The seeded list is authoritative; fall back to the known codes if the
  // endpoint is unavailable so provisioning still works.
  const typeOptions =
    walletTypes && walletTypes.length > 0
      ? walletTypes
          .filter((t) => !t.isSystem)
          .map((t) => ({ code: t.code, name: t.name, ownerType: t.ownerType }))
      : WALLET_TYPE_CODES.map((code) => ({
          code,
          name: humanizeCode(code),
          ownerType: '' as string,
        }));

  const relevantTypeOptions = typeOptions.filter(
    (t) => !t.ownerType || t.ownerType === provisionOwnerType,
  );

  return (
    <PageGuard permission="wallets.read">
      <div className="space-y-8">
        {/* Header */}
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
            Finance
          </p>
          <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
            Treasury
          </h1>
          <p className="max-w-3xl text-[14px] text-[var(--text-muted)]">
            Move money by hand when the automated flows could not: refunds that never
            landed, holds stuck on finished orders, corrections, and wallets that were
            never created. Every operation is previewed before it is applied and posts
            a balanced journal entry.
          </p>
        </div>

        {/* Standing warning — this page is real money */}
        <div className="flex items-start gap-3 rounded-3xl border border-[var(--state-warning-border)] bg-[var(--state-warning-bg)] p-6">
          <ShieldAlert
            className="mt-0.5 h-5 w-5 shrink-0 text-[var(--state-warning)]"
            strokeWidth={1.5}
          />
          <div className="space-y-1">
            <p className="text-[14px] font-semibold text-[var(--text-primary)]">
              These operations move real money
            </p>
            <p className="text-[13px] text-[var(--text-secondary)]">
              Every operation runs as a dry run first and shows the exact before → after
              balances. Nothing is written until you confirm on that second screen. A
              reason is mandatory and is stored on the journal entry.
            </p>
          </div>
        </div>

        {/* Owner ledger jump + provisioning */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Open an owner ledger */}
          <section className="space-y-4 rounded-3xl bg-[var(--bg-surface)] p-6 shadow-soft">
            <div className="space-y-1">
              <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
                Open an owner ledger
              </h2>
              <p className="text-[13px] text-[var(--text-muted)]">
                Every wallet, hold and withdrawal for one user, business or branch in a
                single view.
              </p>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (jumpOwner) {
                  navigate(`/finance/owners/${jumpOwnerType}/${jumpOwner.ownerId}`);
                }
              }}
              className="space-y-4"
            >
              <OwnerTypeToggle
                label="Owner type"
                value={jumpOwnerType}
                onChange={(v) => setJumpOwnerType(v)}
              />

              <OwnerPicker
                label="Who"
                ownerType={jumpOwnerType}
                value={jumpOwner}
                onChange={setJumpOwner}
                hint="Search by name — check the phone or email on the result before opening."
              />

              <button
                type="submit"
                disabled={!jumpOwner}
                className="inline-flex items-center gap-2 rounded-full bg-[var(--text-primary)] px-5 py-2.5 text-[14px] font-medium text-[var(--bg-surface)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Open ledger
                <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </form>
          </section>

          {/* Provision a wallet */}
          <section className="space-y-4 rounded-3xl bg-[var(--bg-surface)] p-6 shadow-soft">
            <div className="space-y-1">
              <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
                Provision a wallet
              </h2>
              <p className="text-[13px] text-[var(--text-muted)]">
                A payout fails silently when the destination wallet was never created.
                Creating it here is idempotent — if it already exists, nothing changes.
              </p>
            </div>

            <div className="space-y-4">
              <OwnerTypeToggle
                label="Owner type"
                value={provisionOwnerType}
                onChange={(v) => {
                  setProvisionOwnerType(v);
                  setProvisionTypeCode('');
                }}
              />

              <OwnerPicker
                label="Who the wallet belongs to"
                ownerType={provisionOwnerType}
                value={provisionOwner}
                onChange={setProvisionOwner}
              />

              <div>
                <label className={labelClass} htmlFor="provision-type">
                  Wallet type
                </label>
                <select
                  id="provision-type"
                  value={provisionTypeCode}
                  onChange={(e) => setProvisionTypeCode(e.target.value)}
                  className={cn(inputClass, 'w-full')}
                >
                  <option value="">Select a type...</option>
                  {relevantTypeOptions.map((t) => (
                    <option key={t.code} value={t.code}>
                      {t.name || humanizeCode(t.code)}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={() => provision.mutate()}
                disabled={!canProvision || provision.isPending}
                className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <PlusCircle className="h-4 w-4" strokeWidth={1.5} />
                {provision.isPending ? 'Provisioning...' : 'Provision wallet'}
              </button>
            </div>
          </section>
        </div>

        {/* Wallet finder */}
        <section className="space-y-4">
          <div className="space-y-1">
            <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
              Find a wallet to operate on
            </h2>
            <p className="text-[13px] text-[var(--text-muted)]">
              Search by owner name, then manage money directly on the wallet.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setQ(search.trim());
                setPage(1);
              }}
              className="relative"
            >
              <Search
                className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-muted)]"
                strokeWidth={1.5}
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search owner name..."
                className={cn(inputClass, 'w-72 pl-10')}
              />
            </form>

            <select
              value={ownerType}
              onChange={(e) => {
                setOwnerType(e.target.value as '' | WalletOwnerType);
                setPage(1);
              }}
              className={cn(inputClass, 'w-48')}
            >
              <option value="">All owners</option>
              <option value="system">Chekit platform</option>
              <option value="business">Business</option>
              <option value="branch">Branch</option>
              <option value="user">User</option>
            </select>

            <select
              value={walletTypeCode}
              onChange={(e) => {
                setWalletTypeCode(e.target.value);
                setPage(1);
              }}
              className={cn(inputClass, 'w-56')}
            >
              <option value="">All wallet types</option>
              {WALLET_TYPE_CODES.map((code) => (
                <option key={code} value={code}>
                  {humanizeCode(code)}
                </option>
              ))}
            </select>

            {isFetching && !isLoading && (
              <span className="text-[13px] text-[var(--text-muted)]">Updating...</span>
            )}
          </div>

          <div className="overflow-hidden rounded-3xl bg-[var(--bg-surface)] shadow-soft">
            {isLoading && (
              <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
                Loading wallets...
              </div>
            )}

            {!!error && !isLoading && (
              <div className="p-12 text-center text-[14px] text-[var(--state-danger)]">
                Failed to load wallets.
              </div>
            )}

            {data && !isLoading && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[var(--bg-base)]">
                    <tr>
                      <th className={thClass}>Wallet</th>
                      <th className={thClass}>Owner</th>
                      <th className={thClass}>Balance</th>
                      <th className={thClass}>Held</th>
                      <th className={thClass}>Available</th>
                      <th className={thClass}>Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-subtle)]">
                    {wallets.map((w) => (
                      <tr key={w.walletId} className="transition-colors hover:bg-[var(--bg-base)]">
                        <td className="px-6 py-4">
                          <div className="text-[14px] font-medium text-[var(--text-primary)]">
                            {w.typeName || humanizeCode(w.typeCode)}
                          </div>
                          <div className="text-[12px] text-[var(--text-muted)]">{w.typeCode}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="max-w-[240px] truncate text-[14px] text-[var(--text-primary)]">
                            {w.ownerLabel ?? '—'}
                          </div>
                          <div className="text-[12px] text-[var(--text-muted)]">
                            {ownerTypeLabel[w.ownerType] ?? w.ownerType}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-[14px] text-[var(--text-primary)]">
                          {formatCurrency(w.balance, w.currency)}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-[14px] text-[var(--state-warning)]">
                          {formatCurrency(w.heldBalance, w.currency)}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5 text-[14px] font-medium',
                              w.availableBalance < 0
                                ? 'text-[var(--state-danger)]'
                                : 'text-[var(--text-primary)]',
                            )}
                          >
                            {w.availableBalance < 0 && (
                              <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} />
                            )}
                            {formatCurrency(w.availableBalance, w.currency)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            type="button"
                            onClick={() => setTarget(w)}
                            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-[var(--border-default)] px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)]"
                          >
                            <Banknote className="h-4 w-4" strokeWidth={1.5} />
                            Manage money
                          </button>
                        </td>
                      </tr>
                    ))}

                    {wallets.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-6 py-16 text-center text-[14px] text-[var(--text-muted)]"
                        >
                          No wallets match these filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {data && totalCount > 0 && (
              <div className="flex items-center justify-between gap-4 border-t border-[var(--border-subtle)] px-6 py-4">
                <span className="text-[13px] text-[var(--text-muted)]">
                  Page {page} of {lastPage} · {totalCount} wallets
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="rounded-full border border-[var(--border-default)] px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                    disabled={page >= lastPage}
                    className="rounded-full border border-[var(--border-default)] px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      {target && (
        <TreasuryOperationModal
          open
          onClose={() => setTarget(null)}
          walletId={target.walletId}
          walletLabel={target.ownerLabel}
          walletTypeCode={target.typeCode}
        />
      )}
    </PageGuard>
  );
};
